import React, { useState, useRef, useEffect, useContext } from 'react';
import {
    Box,
    Stack,
    TextField,
    Paper,
    Typography,
    Button,
    Divider,
    Alert,
    Chip
} from '@mui/material';
import {
    Send as SendIcon,
    Refresh as RefreshIcon,
    SmartToy as AIIcon,
    Cancel as CancelIcon
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { 
    useAgentChatMutation
} from '../../../api/apiSlice';
import { UserContext } from '../../Hooks/useUser';
import MessageBubble from './MessageBubble';

function DataExplorationAgent() {
    const [message, setMessage] = useState('');
    const [conversationId, setConversationId] = useState(null);
    const [conversationTitle, setConversationTitle] = useState(null);
    const [chatMessages, setChatMessages] = useState([]);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Get user from UserContext
    const { user } = useContext(UserContext);
    const userId = user?.id;

    const messagesEndRef = useRef(null);
    const currentRequestRef = useRef(null);

    // Mutations and queries
    const [agentChat, { isLoading: isChatLoading }] = useAgentChatMutation();

    // Auto-scroll to bottom when new messages are added
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [chatMessages]);

    // Cleanup requests on unmount
    useEffect(() => {
        return () => {
            if (currentRequestRef.current) {
                currentRequestRef.current.abort();
            }
        };
    }, []);

    const handleSendMessage = async () => {
        if (!message.trim() || !userId) return;

        const userMessage = {
            id: Date.now(),
            content: message,
            role: 'user',
            timestamp: new Date(),
            userId
        };

        setChatMessages(prev => [...prev, userMessage]);
        setMessage('');
        setError(null);

        try {
            // Store the current request for potential cancellation
            const requestPromise = agentChat({
                message: message.trim(),
                userId,
                conversationId
            });
            currentRequestRef.current = requestPromise;
            
            const response = await requestPromise.unwrap();

            // Update conversation ID and title from response
            if (response.conversation_id) {
                setConversationId(response.conversation_id);
            }
            if (response.conversation_title) {
                setConversationTitle(response.conversation_title);
            }

            const aiMessage = {
                id: Date.now() + 1,
                content: response.message || response.response || 'No response received',
                role: 'ai',
                timestamp: new Date(),
                userId,
                conversationId: response.conversation_id,
                metadata: response.metadata
            };

            setChatMessages(prev => [...prev, aiMessage]);
            setSuccess('Message sent successfully!');

        } catch (err) {
            // Check if the error is due to request cancellation
            if (err.name === 'AbortError' || err.message?.includes('aborted')) {
                setSuccess('Request cancelled');
                return;
            }
            
            console.error('Chat error:', err);
            setError(err.data?.message || err.message || 'Failed to send message');
            
            const errorMessage = {
                id: Date.now() + 1,
                content: 'Error: ' + (err.data?.message || err.message || 'Failed to get response'),
                role: 'ai',
                timestamp: new Date(),
                userId,
                isError: true
            };
            
            setChatMessages(prev => [...prev, errorMessage]);
        } finally {
            // Clear the current request reference
            currentRequestRef.current = null;
        }
    };

    const handleNewConversation = () => {
        setConversationId(null);
        setConversationTitle(null);
        setChatMessages([]);
        setError(null);
        setSuccess('Started new conversation');
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleSendMessage();
        }
    };

    const handleCancelRequest = () => {
        if (currentRequestRef.current) {
            currentRequestRef.current.abort();
            currentRequestRef.current = null;
        }
    };

    return (
        <Paper elevation={2} sx={{ m: 2, p: 3, height: '80vh', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <AIIcon color="primary" />
                    <Typography variant="h6">Data Exploration Agent</Typography>
                    <Chip label="Beta" size="small" color="info" variant="outlined" />
                    {conversationTitle && (
                        <Chip 
                            label={`"${conversationTitle}"`} 
                            size="small" 
                            color="info" 
                            sx={{ maxWidth: 200 }} 
                        />
                    )}
                </Stack>
                
                <Stack direction="row" spacing={1}>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={handleNewConversation}
                        size="small"
                        color="success"
                    >
                        New Chat
                    </Button>
                </Stack>
            </Stack>

            {/* Status Messages */}
            {!user && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    No user logged in. Please log in to use the Data Exploration Agent.
                </Alert>
            )}
            
            {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}
            
            {success && (
                <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
                    {success}
                </Alert>
            )}

            <Divider sx={{ mb: 2 }} />

            {/* Messages Area */}
            <Box sx={{ flex: 1, overflow: 'auto', mb: 2 }}>
                <Stack spacing={1}>
                    {chatMessages.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                            <AIIcon sx={{ fontSize: 48, mb: 2, opacity: 0.3 }} />
                            <Typography>No messages yet. Send a message to start exploring your data!</Typography>
                        </Box>
                    ) : (
                        chatMessages.map((msg) => (
                            <MessageBubble key={msg.id} message={msg} />
                        ))
                    )}
                    <div ref={messagesEndRef} />
                </Stack>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Input Area */}
            <Stack direction="row" spacing={2} alignItems="flex-end">
                <TextField
                    fullWidth
                    multiline
                    maxRows={3}
                    variant="outlined"
                    placeholder={userId ? 'Ask about your data...' : 'Please log in to chat'}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={isChatLoading || !userId}
                />
                
                {isChatLoading ? (
                    <Button
                        variant="outlined"
                        onClick={handleCancelRequest}
                        color="error"
                        sx={{ minWidth: '56px', height: '56px' }}
                    >
                        <CancelIcon />
                    </Button>
                ) : (
                    <LoadingButton
                        variant="contained"
                        onClick={handleSendMessage}
                        disabled={!message.trim() || !userId}
                        loading={isChatLoading}
                        sx={{ minWidth: '56px', height: '56px' }}
                    >
                        <SendIcon />
                    </LoadingButton>
                )}
            </Stack>
        </Paper>
    );
}

export default DataExplorationAgent;